export namespace MyCampApp
{
    export enum Environment
    {
        Prod,
        Staging,
        Dev,
        Test,
        CampSiteBeta,
    }

    export enum HttpResponseStatusCode
    {
        Offline = 0,
        BadRequest = 400,
        Unauthorized = 401
    }

    export enum SortOrder
    {
        Ascending,
        Descending
    }

    export enum AppSection
    {
        Unknown,
        News,
        Events,
        Notifications,
        Members,
        Albums,
        Contacts,
        Categories,
        OrganizationInfo,
        Locations,
        Sponsors,
        Themes,
        DocumentCenter,
        TextMessages,
    }

    export enum CategoryItemType
    {
        Other,
        Group,
        Session,
        Bus,
        Location,
    }

    export function getCategoryItemTypeString(type: CategoryItemType): string
    {
        switch (type)
        {
            case CategoryItemType.Other: return 'Other';
            case CategoryItemType.Group: return 'Group';
            case CategoryItemType.Session: return 'Session';
            case CategoryItemType.Bus: return 'Bus';
            case MyCampApp.CategoryItemType.Location: return 'Location';
        }
    }

    export function getCategoryItemTypeColor(type: CategoryItemType): string
    {
        switch (type)
        {
            case CategoryItemType.Other: return '#AEE5CF';
            case CategoryItemType.Group: return '#AEC4E5';
            case CategoryItemType.Session: return '#E5AEC4';
            case CategoryItemType.Bus: return '#DFE5AE';
            case CategoryItemType.Location: return '#AEDFE5';
        }
    }

    export function isCategoryItemTypeSortedBefore(categoryItemType: MyCampApp.CategoryItemType, otherCategoryItemType: MyCampApp.CategoryItemType): number
    {
        if (categoryItemType === otherCategoryItemType) { return 0; }
        switch (categoryItemType)
        {
            case MyCampApp.CategoryItemType.Session: return -1; // Session is first
            case MyCampApp.CategoryItemType.Group: return otherCategoryItemType === MyCampApp.CategoryItemType.Session ? 1 : -1; // Group is second
            case MyCampApp.CategoryItemType.Bus: return otherCategoryItemType === MyCampApp.CategoryItemType.Group || otherCategoryItemType === MyCampApp.CategoryItemType.Session ? 1 : -1; // Bus is third
            case MyCampApp.CategoryItemType.Location: return otherCategoryItemType === MyCampApp.CategoryItemType.Group ||
            otherCategoryItemType === MyCampApp.CategoryItemType.Session || otherCategoryItemType === MyCampApp.CategoryItemType.Bus ? 1 : -1; // Location is forth
            case MyCampApp.CategoryItemType.Other: return 1; // Other is last
        }
    }

    export enum DocumentType
    {
        None,
        TermsOfService,
        PrivacyPolicy,
        FAQ,
    }

    export enum AssetType
    {
        Image = 1,
        Video = 2
    }

    export enum CheckInCheckOutActivityType
    {
        Unknown,
        CheckInAnnounced,
        CheckInOnBus,
        CheckInConfirmed,
        CheckOutAnnounced,
        CheckOutOnBus,
        CheckOutReleasedToGuardian,
        CheckOutScheduled
    }

    export function activityTypeValue(activityType: CheckInCheckOutActivityType | undefined) { return JSON.stringify(activityType); }
    export function activityTypeFromString(stringValue: string): CheckInCheckOutActivityType | undefined {
        if (stringValue == undefined) return undefined;
        switch (stringValue)
        {
            case CheckInCheckOutActivityType.CheckInAnnounced.toString(): return CheckInCheckOutActivityType.CheckInAnnounced;
            case CheckInCheckOutActivityType.CheckInOnBus.toString(): return CheckInCheckOutActivityType.CheckInOnBus;
            case CheckInCheckOutActivityType.CheckInConfirmed.toString(): return CheckInCheckOutActivityType.CheckInConfirmed;
            case CheckInCheckOutActivityType.CheckOutAnnounced.toString(): return CheckInCheckOutActivityType.CheckOutAnnounced;
            case CheckInCheckOutActivityType.CheckOutOnBus.toString(): return CheckInCheckOutActivityType.CheckOutOnBus;
            case CheckInCheckOutActivityType.CheckOutReleasedToGuardian.toString(): return CheckInCheckOutActivityType.CheckOutReleasedToGuardian;
            case CheckInCheckOutActivityType.CheckOutScheduled.toString(): return CheckInCheckOutActivityType.CheckOutScheduled;
            default: return undefined;
        }
    }

    export function activityTypeString(activityType: CheckInCheckOutActivityType | undefined)
    {
        switch (activityType)
        {
            case CheckInCheckOutActivityType.CheckInAnnounced: return 'Ready for Check-In';
            case CheckInCheckOutActivityType.CheckInOnBus: return 'Inbound On Bus';
            case CheckInCheckOutActivityType.CheckInConfirmed: return 'Arrived';
            case CheckInCheckOutActivityType.CheckOutAnnounced: return 'Ready for Check-Out';
            case CheckInCheckOutActivityType.CheckOutOnBus: return 'Outbound On Bus';
            case CheckInCheckOutActivityType.CheckOutReleasedToGuardian: return 'Released';
            case CheckInCheckOutActivityType.CheckOutScheduled: return 'Scheduled for Check-Out';
            default: return 'Unknown';
        }
    }

    export function isCheckInActivity(activityType: CheckInCheckOutActivityType): boolean { return activityType >= 1 && activityType <= 3; }
    export function isCheckOutActivity(activityType: CheckInCheckOutActivityType): boolean { return activityType >= 4 && activityType <= 7; }
    export function isCheckOutTransitionActivity(activityType: CheckInCheckOutActivityType): boolean { return activityType >= 5 && activityType <= 6; }

    export enum CheckInCheckOutValidationTypes
    {
        None = 0x00,
        Visual = 0x01,
        PictureId = 0x02,
        PinCode = 0x04,
        Placard = 0x08,
        MustHaveBeenDroppedOff = 0x10,
    }

    export function validationTypeString(validationType?: CheckInCheckOutValidationTypes)
    {
        if (validationType === undefined) return 'Unknown';

        switch (validationType)
        {
            case CheckInCheckOutValidationTypes.None: return 'None';
            case CheckInCheckOutValidationTypes.Visual: return 'Visual';
            case CheckInCheckOutValidationTypes.PictureId: return 'Picture ID';
            case CheckInCheckOutValidationTypes.PinCode: return 'Pin Code';
            case CheckInCheckOutValidationTypes.Placard: return 'Placard';
            case CheckInCheckOutValidationTypes.MustHaveBeenDroppedOff: return 'Must be Checked-In First';
        }
    }

    export enum OrganizationDataSyncType
    {
        None = 0,
        CampDoc,
        CampMinder,
        CampBrain,
        ActiveNetwork,
        CampSite
    }

    export enum RecurrenceType
    {
        Never = -1,
        Daily = 0,
        Weekly = 1,
        Monthly = 2,
        Yearly = 3,
        Custom
    }

    export enum NotificationType
    {
        Email = 1,
        Push = 2,
        Sms = 3,
    }

    export enum RegistrationFilterType
    {
        All,
        Registered,
        Unregistered
    }

	export enum RolloverOptionType
	{
		Keep = 1,
		Remove = 2,
		Unpublish = 3
	}

	export enum SocialMediaAccountType
    {
        Facebook = 1,
        Twitter = 2,
        Instagram = 5,
    }

    export enum ProductFeatureFlags
    {
        ProfessionalInfo = 0x01,
        Sponsor = 0x02,
        Staff = 0x04,
        HoursOfOperation = 0x08,
        GuestLoginPassword = 0x10,
        GuestLoginAnonymous = 0x20
    }

    export enum UserReportType
    {
        Registrations = 1,
        Logins = 2,
    }

    export enum UserActionType
    {
        Unknown,
        Login,                // AdditionalDataId = LoginType, AdditionalDataJson = { DeviceType: DeviceType }
        ViewSelected,        // AdditionalDataId = ViewType
        AlbumViewed,         // AdditionalDataId = Album Id
        ContentShared,       // AdditionalDataId = ContentType, AdditionalDataJson = { SharedTo: ShareToType }
        OrgInfoRequested,    // AdditionalDataId = OrgInfoType
        MoreInfoSelected,    // AdditionalDataId = MoreInfoType
        SponsorViewed,        // AdditionalDataId = Sponsor Id
        SponsorLinkViewed,    // AdditionalDataId = Sponsor Id
        MemberCommunication,    // AdditionalDataId = Member Id, AdditionalDataJson = { ContactType: ContactType }
        ContactCommunication,    // AdditionalDataId = Contact Id, AdditionalDataJson = { ContactType: ContactType }
        UserRegistration,
        NewsItemViewed,     // AdditionalDataId: News Item Id
        EventViewed         // AdditionalDataId: Event Id
    }

    export enum TextMessageType
    {
        FromMember = 0,
        FromOrganization = 1
    }

    export function getUserActionTypeString(actionType: UserActionType): string
    {
        switch (actionType)
        {
            case UserActionType.Login: return 'Login';
            case UserActionType.ViewSelected: return 'View Selected';
            case UserActionType.AlbumViewed: return 'Viewed Photo Album';
            case UserActionType.ContentShared: return 'Shared Content';
            case UserActionType.OrgInfoRequested: return 'Requested Camp Info';
            case UserActionType.MoreInfoSelected: return 'Requested More Info';
            case UserActionType.SponsorViewed: return 'Viewed Sponsor';
            case UserActionType.SponsorLinkViewed: return 'Clicked Sponsor Link';
            case UserActionType.MemberCommunication: return 'Contacted Other Member';
            case UserActionType.ContactCommunication: return 'Contacted Camp';
            case UserActionType.UserRegistration: return 'Registered';
            default: return 'Unknown';
        }
    }

	export enum LogEntryActionType
	{
		Unknown = 0,
		CreatedOrg = 67,
		UpdatedOrg = 2,
		UpdatedOrgRestrictedSettings = 68,
		CreatedTheme = 33,
		UpdatedTheme = 34,
		DeletedTheme = 35,
		PromotedAppleBuild = 69,
		PromotedGoogleBuild = 70,
		UpdatedGlobalAdminPassword = 74,
		UpdatedOrgAdminPassword = 75,
		UpdatedMemberPassword = 76,
		SentMemberPasswordResetEmail = 77,
		SentGlobalAdminPasswordResetEmail = 78,
		SentOrgAdminPasswordResetEmail = 79,
	}

	export function getLogEntryActionTypeString(actionType: LogEntryActionType): string
	{
		switch (actionType)
		{
			case LogEntryActionType.CreatedOrg: return 'Created Organization';
			case LogEntryActionType.UpdatedOrg: return 'Updated Organization Settings';
			case LogEntryActionType.UpdatedOrgRestrictedSettings: return 'Updated Organization Setup Settings';
			case LogEntryActionType.CreatedTheme: return 'Created Theme';
			case LogEntryActionType.UpdatedTheme: return 'Updated Theme';
			case LogEntryActionType.DeletedTheme: return 'Deleted Theme';
			case LogEntryActionType.PromotedAppleBuild: return 'Promoted Apple Build';
			case LogEntryActionType.PromotedGoogleBuild: return 'Promoted Google Build';
			case LogEntryActionType.UpdatedGlobalAdminPassword: return 'Updated Global Admin Password';
			case LogEntryActionType.UpdatedOrgAdminPassword: return 'Updated Org Admin Password';
			case LogEntryActionType.UpdatedMemberPassword: return 'Updated Member Password';
			case LogEntryActionType.SentMemberPasswordResetEmail: return 'Sent Member Password Reset Email';
			case LogEntryActionType.SentGlobalAdminPasswordResetEmail: return 'Sent Global Admin Password Reset Email';
			case LogEntryActionType.SentOrgAdminPasswordResetEmail: return 'Sent Org Admin Password Reset Email';
			default: return 'Unknown';
		}
	}

	export enum ViewType
    {
        Unknown,
        News,
        Events,
        Photos,
        Contacts,
        More,
        NotificationList
    }

    export function getViewTypeString(viewType: ViewType): string
    {
        switch (viewType)
        {
            case ViewType.News: return 'News';
            case ViewType.Events: return 'Calendar';
            case ViewType.Photos: return 'Photos';
            case ViewType.Contacts: return 'Contacts';
            case ViewType.More: return 'More';
            case ViewType.NotificationList: return 'NotificationList';
            default: return 'Unknown';
        }
    }

    export enum MoreInfoType
    {
        Unknown,
        OrgInfo,
        Profile,
        Settings,
        Sponsor,
        Location,
        Link
    }

    export function getMoreInfoTypeString(moreInfoType: MoreInfoType): string
    {
        switch (moreInfoType)
        {
            case MoreInfoType.OrgInfo: return 'Camp Info';
            case MoreInfoType.Profile: return 'Profile';
            case MoreInfoType.Settings: return 'Settings';
            case MoreInfoType.Sponsor: return 'Sponsor';
            case MoreInfoType.Location: return 'Location';
            case MoreInfoType.Link: return 'Link';
            default: return 'Unknown';
        }
    }

    export var callToActionChoices = [
        { label: '[ NONE ]', value: null },
        { label: 'RSVP', value: 'RSVP' },
        { label: 'Enroll', value: 'Enroll' },
        { label: 'GO', value: 'GO' },
        { label: 'Register', value: 'Register' },
        { label: 'Take the Survey', value: 'Take the Survey' },
        { label: 'Online Waiver', value: 'Online Waiver' },
    ];
}
